import { useEffect, useState } from 'react';
import { MESSAGE_MAX, POINTS, type ReportInput, type ReportKind, type ReportResponse, levelFor, normalizeTagId } from '@notlost/shared';
import { PinIcon, ShieldCheck } from '../components/Brand';
import { Page } from '../components/Layout';
import { ApiError, getTagStatus, sendReport } from '../lib/api';
import { getHelperId, ownsTag } from '../lib/helper';
import { navigate } from '../lib/router';
import { t } from '../i18n';

type Phase = 'loading' | 'unavailable' | 'ready' | 'locating' | 'sending' | 'done';
type Notice = 'locationDenied' | 'locationFailed' | 'sendFailed' | null;

const FALLBACKS: Exclude<ReportKind, 'location'>[] = ['lost_and_found', 'staff', 'unsure'];

/** Public page behind the QR code. Never shows anything about the owner or the item. */
export function Finder({ rawId }: { rawId: string }) {
  const tagId = normalizeTagId(rawId);
  const [phase, setPhase] = useState<Phase>('loading');
  const [notice, setNotice] = useState<Notice>(null);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState('');
  const [sentAccuracy, setSentAccuracy] = useState<number | null>(null);
  const [result, setResult] = useState<ReportResponse | null>(null);
  const own = tagId ? ownsTag(tagId) : false;

  useEffect(() => {
    if (!tagId) {
      setPhase('unavailable');
      return;
    }
    if (window.location.pathname !== `/t/${tagId}`) window.history.replaceState(null, '', `/t/${tagId}`);
    getTagStatus(tagId)
      .then((r) => setPhase(r.status === 'active' ? 'ready' : 'unavailable'))
      .catch(() => setPhase('unavailable'));
  }, [tagId]);

  async function submit(report: ReportInput) {
    if (!tagId) return;
    setPhase('sending');
    setNotice(null);
    try {
      // No points for scanning your own tag.
      const res = await sendReport(tagId, { ...report, message: message.trim() || undefined }, own ? null : getHelperId(true));
      setResult(res);
      setSentAccuracy(report.kind === 'location' ? (report.accuracy ?? null) : null);
      setPhase('done');
      navigator.vibrate?.(30);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setPhase('unavailable');
      else {
        setNotice('sendFailed');
        setPhase('ready');
      }
    }
  }

  function shareLocation() {
    if (!('geolocation' in navigator)) {
      setNotice('locationFailed');
      return;
    }
    setPhase('locating');
    setNotice(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => void submit({ kind: 'location', lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => {
        setNotice(err.code === err.PERMISSION_DENIED ? 'locationDenied' : 'locationFailed');
        setPhase('ready');
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 },
    );
  }

  const busy = phase === 'locating' || phase === 'sending';

  return (
    <Page minimalHeader>
      {phase === 'loading' && <p className="mt-10 text-lg text-muted">{t('finder.loading')}</p>}

      {phase === 'unavailable' && (
        <section className="card mt-4 p-6">
          <h1 className="text-2xl font-semibold tracking-tight">{t('finder.unavailable.title')}</h1>
          <p className="mt-3 leading-relaxed text-body">{t('finder.unavailable.body')}</p>
        </section>
      )}

      {phase === 'done' && (
        <section aria-live="polite" className="flex flex-col gap-4">
          <div className="card p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-50 text-success">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <h1 className="mt-4 text-2xl font-semibold tracking-tight leading-tight">{t('finder.done.title')}</h1>
            <p className="mt-2 leading-relaxed text-body">{t('finder.done.body')}</p>
            {sentAccuracy !== null && <p className="mt-2 text-sm text-muted">{t('finder.done.accuracy', { meters: Math.round(sentAccuracy) })}</p>}
          </div>
          <PointsCard result={result} own={own} />
        </section>
      )}

      {(phase === 'ready' || busy) && (
        <>
          <p className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700">
            <ShieldCheck className="h-4 w-4" />
            {t('finder.trust')}
          </p>
          <h1 className="headline mt-4 text-[34px]">{t('finder.title')}</h1>
          <p className="mt-3 text-lg leading-relaxed text-body">{t('finder.body')}</p>

          <div className="mt-5">
            {!showMessage ? (
              <button type="button" onClick={() => setShowMessage(true)} className="min-h-11 font-semibold text-blue underline underline-offset-4">
                {t('finder.messageToggle')}
              </button>
            ) : (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="note" className="text-sm font-semibold">
                  {t('finder.messageLabel')}
                </label>
                <textarea
                  id="note"
                  rows={3}
                  maxLength={MESSAGE_MAX}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('finder.messagePlaceholder')}
                  className="field py-3"
                />
                <span className="self-end text-xs text-muted">{t('finder.messageCount', { count: message.length, max: MESSAGE_MAX })}</span>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={shareLocation}
            disabled={busy}
            className="btn btn-primary mt-6 min-h-16 w-full text-[21px] font-semibold"
          >
            <PinIcon className="h-7 w-7" />
            {phase === 'locating' ? t('finder.locating') : phase === 'sending' ? t('finder.sending') : t('finder.share')}
          </button>
          <p className="mt-3 text-center text-sm text-muted">{t('finder.privacy')}</p>

          {notice && (
            <p role="alert" className="mt-5 rounded-xl border border-amber bg-amber-50 p-4 font-medium text-ink">
              {t(`finder.${notice}`)}
            </p>
          )}

          <h2 className="mt-8 text-sm font-semibold text-muted">{t('finder.or')}</h2>
          <div className="mt-3 flex flex-col gap-3">
            {FALLBACKS.map((kind) => (
              <button
                key={kind}
                type="button"
                disabled={busy}
                onClick={() => void submit({ kind })}
                className="flex min-h-14 items-center rounded-[14px] bg-white px-5 text-left text-[17px] text-ink shadow-card transition hover:bg-blue-50 disabled:opacity-60"
              >
                {t(`finder.kind.${kind}`)}
              </button>
            ))}
          </div>
        </>
      )}
    </Page>
  );
}

/** The gamified thank you: points earned, level progress, link to the helper card. */
function PointsCard({ result, own }: { result: ReportResponse | null; own: boolean }) {
  if (own) return <p className="card p-5 text-body">{t('finder.points.own')}</p>;
  const helper = result?.helper;
  if (!helper) return null;
  const level = levelFor(helper.points);
  return (
    <div className="card overflow-hidden">
      <div className="bg-night p-5 text-white">
        {helper.awarded > 0 ? (
          <p className="text-3xl font-semibold tracking-tight">{t('finder.points.earned', { n: helper.awarded })}</p>
        ) : (
          <p className="font-semibold">{t('finder.points.none')}</p>
        )}
        <p className="mt-1 text-night-muted">{t('helper.level', { level: level.level, name: level.name })}</p>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/20">
          <div className="h-full rounded-full bg-amber" style={{ width: `${Math.round(level.progress * 100)}%` }} />
        </div>
        <p className="mt-2 text-sm text-night-muted">
          {level.nextAt !== null ? t('helper.next', { n: level.nextAt - helper.points, name: level.nextName ?? '' }) : t('helper.max')}
        </p>
      </div>
      <div className="flex flex-col gap-3 p-5">
        <p className="text-sm text-body">{t('finder.points.bonus', { n: POINTS.returned })}</p>
        <button type="button" onClick={() => navigate('/helper')} className="btn btn-secondary">
          {t('finder.points.card')}
        </button>
      </div>
    </div>
  );
}
